import { defineStore } from "pinia";
import { db, uploadImage, functions } from "@/mixins/firebase";
import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  query,
  where,
  or,
  and,
  updateDoc,
  orderBy,
  addDoc,
  onSnapshot,
  serverTimestamp,
  sum,
  getAggregateFromServer,
  deleteDoc,
  writeBatch,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import {
  getMaintenanceEvents,
  getMetadataFor,
  getMetadataForMmids,
  getStoragePercentage,
} from "@firebasegen/default-connector";
import { WashingMachine } from "./washingmachines";
import { MB2RecyclingRate, MB2StatsDiff } from "@/mixins/calculations";
import { RawStat, Stat, useStatsStore } from "./stats";
import { getMetroArea } from "@/mixins/findplace";
import {
  NOTIFICATION_SETTING,
  NotificationSetting,
  Subscriber,
} from "@/mixins/notificationSettingsType";
import { useAuthStore } from "./auth";
import * as Sentry from "@sentry/vue";

export interface Geodata {
  lat: number;
  lng: number;
  street: string;
  city: string;
  country: string;
  postcode: string;
  group_as?: string; // If city name is a suburb, filtering should be done based on this
  tried_grouped_as?: boolean; // If we have tried to set group_as before
}

export interface Install {
  geodata?: Geodata;
  name: string;
  customer?: string;
  contact?: {
    name: string;
    email: string;
    phone: string;
    role?: string;
  }[];
  date?: Date | string;
  installed?: boolean;
}
export type MimboxTag = string | { name: string; color?: string };
export interface Mimbox {
  id: string;
  mimbox_vers: string;
  serial_nr: string;
  hidePerformanceData?: boolean;
  access: string[];
  images: string[];
  water_saved?: number;
  water_used?: number;
  mmid?: string;
  iccid?: string;
  company?: string; // NOT USED, only here for legacy reasons
  sw_ver: string;
  admin_page?: {
    [key: string]: any;
  };
  historical?: {
    waterUsed: number;
    waterSaved: number;
    ts: number;
  };
  install?: Install;
  updatedAt?: string | number;
  connectedWashingMachines?: WashingMachine[];
  status?: string;
  tags?: MimboxTag[];
  internalName?: string;
  has_service_agreement?: boolean;
}

interface WaterData {
  loading: boolean;
  total: {
    processed: number;
    saved: number;
  };
  since: {
    timestamp: number;
    processed: number;
    saved: number;
  };
}

interface ServiceLog {
  timestamp: number;
  [key: string]: any;
}

interface EolLog {
  timestamp: number;
  [key: string]: any;
}

interface MimboxData extends Partial<Mimbox> {
  newTags: MimboxTag[];
  files?: File[];
  qrLink?: string;
}

interface C1Data {
  name: string;
  [key: string]: any;
}

interface EventStatsCache {
  [key: string]: {
    stats: { waterSaved: number; waterUsed: number };
    timestamp: number;
  };
}

import { documentId } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { mb1AggregateLimiter } from "@/utils/concurrency";
import { sentryClient } from "@/sentry";
import { mimboxConverter } from "@mimsights-shared/converters/frontend";

let deltaRRMB1Queue: Array<{
  id: string;
  stat: Stat;
  startDelta: Date;
  endDelta: Date;
  resolve: (val: number | undefined) => void;
  reject: (err: any) => void;
}> = [];
let deltaRRMB1Timeout: ReturnType<typeof setTimeout> | null = null;

let deltaRRQueue: Array<{
  mmid: string;
  stat: Stat;
  beforeFromDelta: Date;
  beforeToDelta: Date;
  startDelta: Date;
  endDelta: Date;
  resolve: (val: number | undefined) => void;
  reject: (err: any) => void;
}> = [];
let deltaRRTimeout: ReturnType<typeof setTimeout> | null = null;

/** 
/ FIXME: this store needs to be removed. But some of its functions are still in used and can be moved to helper functions or inline them where need?
*/
export const useMimboxesStore = defineStore("mimboxes", {
  state: () => {
    return {
      loading: false,
      stats: useStatsStore(),
      selectedMimbox: null as Mimbox | null,
      mimboxes: [] as Mimbox[],
      c1s: [] as any[],
      unsubscribeC1s: null as (() => void) | null,
      water: {
        loading: false,
        total: {
          processed: 0,
          saved: 0,
        },
        since: {
          timestamp: 0,
          processed: 0,
          saved: 0,
        },
      } as WaterData,
      isBootstrapping: false,
      eventStatsCache: {} as EventStatsCache,
      searchFilter: "" as string,
      companiesWithServiceAgreements: {} as Record<string, boolean>,
      deltaRRCache: {} as Record<string, number>,
      deltaRRPromiseCache: {} as Record<string, Promise<number | undefined>>,
      storagePercentageCache: {} as Record<string, number>,
      storagePercentagePromiseCache: {} as Record<string, Promise<Record<string, number>>>,
      swVersionsCache: {} as Record<
        string,
        { espFwVersion: string; cocpuFwVersion: string; ts: string }
      >,
      swVersionsPromiseCache: {} as Record<
        string,
        Promise<Record<string, { espFwVersion: string; cocpuFwVersion: string; ts: string }>>
      >,
      runModeTrackersCache: {} as Record<
        string,
        { firstSeen: string; markedAsManuallyBypassed?: string | null; runMode: number }[]
      >,
      uptimeCache: {} as Record<string, number | null>,
      uptimeKey: "",
    };
  },
  actions: {
    setMimboxes(mimboxes: Mimbox[]) {
      this.mimboxes = mimboxes;
    },
    setMimbox(mimbox: Mimbox) {
      const oldIndex = this.mimboxes.findIndex((m) => m.id === mimbox.id);
      if (oldIndex !== -1) this.mimboxes[oldIndex] = mimbox;
      else this.mimboxes.push(mimbox);
    },
    async selectMimbox(id: string) {
      if (this.selectedMimbox && this.selectedMimbox.id === id) return;
      if (this.mimboxes.length === 0) await this.refresh();
      this.selectedMimbox = this.mimboxes.filter((m) => m.id === id)[0];
      try {
        this.selectedMimbox.connectedWashingMachines = await this.getWashingMachines(
          this.selectedMimbox?.id,
        );
      } catch (error) {
        console.error("Error getting washing machines:", error);
      }
      if (!this.selectedMimbox) throw { message: "This mimbox does not exist" };
    },
    clearSelectedMimbox() {
      this.selectedMimbox = null;
    },
    async refresh() {
      this.loading = true;
      const authStore = useAuthStore();
      const entityId = authStore.idToken?.claims?.entityId;
      const col = collection(db, "mimboxes");

      // Staff see every mimbox. Others see their entity's tree, minus mimboxes that aren't installed.
      let qParam: any[] = [];
      if (!authStore.is.staff()) {
        if (!entityId) {
          this.mimboxes = [];
          this.loading = false;
          return;
        }
        qParam = [
          and(
            or(
              where("customerRef.id", "==", entityId),
              where("customerRef.ancestors", "array-contains", entityId),
            ),
            or(where("status", "==", "installed"), where("mimbox_vers", "==", "MB2")),
          ),
        ];
      }
      const mimboxesQuery = query(col, ...qParam);

      const mimboxesSnapshot = await getDocs(mimboxesQuery);
      const onlineMimboxes = await Promise.all(
        mimboxesSnapshot.docs.map(
          async (doc) => await getDataForMimbox(doc, authStore.is.staff(), authStore.is.staff()),
        ),
      );
      const uniqueCityNamesToLookup = onlineMimboxes.reduce<
        Record<string, { mimboxIds: string[]; postal_code?: string; street?: string }>
      >((acc, curr) => {
        if (curr.install?.geodata?.group_as || curr.install?.geodata?.tried_grouped_as) return acc; // Mimbox is already set
        const city = curr?.install?.geodata?.city;
        if (!city) return acc;
        if (!Object.keys(acc).includes(city)) {
          acc[city] = {
            mimboxIds: [],
            postal_code: curr?.install?.geodata?.postcode,
            street: curr?.install?.geodata?.street,
          };
        }
        acc[city].mimboxIds.push(curr.id as string);
        return acc;
      }, {});
      // Annonym function to try to check the county for a box to be able to easier see for example ALL boxes in Stockholm County.
      // Only roles that may write mimboxes run it: for anyone else the write below is denied, the
      // boxes are never marked as tried, and every refresh would repeat the lookups.
      const canWriteGeodata = authStore.is.anyRole("staff", "super-user");
      (async () => {
        if (!canWriteGeodata || !Object.keys(uniqueCityNamesToLookup).length) return;
        const batch = writeBatch(db);
        let i = 0;
        const len = Object.keys(uniqueCityNamesToLookup).length;
        for (const city in uniqueCityNamesToLookup) {
          const { mimboxIds, postal_code, street } = uniqueCityNamesToLookup[city];
          try {
            const res = await getMetroArea(city, postal_code, street);
            // Wait between requests to avoid rate limiting
            if (i < len - 1) {
              await new Promise((resolve) => setTimeout(resolve, 500));
            }
            if (!res) {
              mimboxIds.forEach((id) => {
                const mimboxDocRef = doc(db, "mimboxes", id);
                batch.set(
                  mimboxDocRef,
                  {
                    install: {
                      geodata: {
                        tried_grouped_as: true,
                      },
                    },
                  },
                  { merge: true },
                );
              });
            } else {
              mimboxIds.forEach((id) => {
                const mimboxDocRef = doc(db, "mimboxes", id);
                batch.set(
                  mimboxDocRef,
                  {
                    install: {
                      geodata: {
                        tried_grouped_as: true,
                        group_as: res,
                      },
                    },
                  },
                  { merge: true },
                );
              });
            }
          } catch (error) {
            console.warn(`Failed to get metro area for ${city}:`, error);
          } finally {
            i++;
          }
        }
        await batch.commit().catch((error) => console.warn("Failed to save metro areas:", error));
      })();

      // Cache resets
      this.deltaRRCache = {};
      this.storagePercentageCache = {};
      this.$patch({
        mimboxes: onlineMimboxes as Mimbox[],
        loading: false,
      });
    },
    async markAsManuallyBypassed(runModeTrackerId: string, mmid: string) {
      const markAsManuallyBypassedFunc = httpsCallable(
        functions,
        "mark_mimbox_as_manually_bypassed",
      );
      await markAsManuallyBypassedFunc({ runModeTrackerId, mmid });
    },
    async hasServiceAgreement(id: string): Promise<boolean> {
      const box = this.mimboxes.find((mimbox) => mimbox.id === id);
      if (!box) return false;
      const companyName = box?.install?.customer;
      if (!companyName) return false;
      if (companyName in this.companiesWithServiceAgreements) {
        return this.companiesWithServiceAgreements[companyName];
      }
      const ref = doc(db, "companies", companyName);
      const companyDoc = await getDoc(ref);
      if (!companyDoc.exists()) return false;
      const hasServiceAgreement = companyDoc.data().serviceAgreement || false;
      this.companiesWithServiceAgreements[companyName] = hasServiceAgreement;
      return hasServiceAgreement;
    },
    async getServiceAgreements(mimboxes: Mimbox[]): Promise<Record<string, boolean>> {
      const companyNames = [
        ...new Set(mimboxes.map((m) => m.install?.customer).filter((n): n is string => !!n)),
      ];

      const uncached = companyNames.filter((n) => !(n in this.companiesWithServiceAgreements));

      if (uncached.length > 0) {
        const companiesRef = collection(db, "companies");
        const CHUNK_SIZE = 30;
        for (let i = 0; i < uncached.length; i += CHUNK_SIZE) {
          const chunk = uncached.slice(i, i + CHUNK_SIZE);
          const q = query(companiesRef, where(documentId(), "in", chunk));
          const snap = await getDocs(q);
          snap.forEach(
            (doc) =>
              (this.companiesWithServiceAgreements[doc.id] = doc.data()?.serviceAgreement ?? false),
          );
        }
      }

      const result: Record<string, boolean> = {};
      mimboxes.forEach((m) => {
        if (m.install?.customer) {
          result[m.id] = this.companiesWithServiceAgreements[m.install.customer] ?? false;
        }
      });
      return result;
    },

    clearWaterData() {
      this.water = {
        loading: false,
        total: {
          processed: 0,
          saved: 0,
        },
        since: {
          timestamp: 0,
          processed: 0,
          saved: 0,
        },
      };
    },
    getCacheKey(id: string, interval?: Date[]): string {
      if (!interval) return `${id}_all`;
      const start = interval[0]?.getTime() ?? 0;
      const end = interval[1]?.getTime() ?? Date.now();
      return `${id}_${start}_${end}`;
    },
    async getEventStats(id: string): Promise<{ waterSaved: number }> {
      const eventsRef = collection(db, "mimboxes", id, "events");
      const eventsQuery = query(eventsRef);

      const snapshot = await getAggregateFromServer(eventsQuery, {
        waterSaved: sum("water_saved"),
      });

      const stats = snapshot.data();

      return stats;
    },

    async getMaintenanceDates(
      mimboxes: Mimbox[],
    ): Promise<Record<string, { monthly: Date | undefined; weekly: Date | undefined }>> {
      const MB2s = mimboxes.filter(({ mmid }) => !!mmid);
      const MB1s = mimboxes.filter(({ mmid }) => !mmid);

      const ret: Record<string, { monthly: Date | undefined; weekly: Date | undefined }> = {};

      const MB1sPromises = MB1s.map((mimbox) =>
        mb1AggregateLimiter(async () => {
          // Read denormalized date from the mimbox document
          const lastMaintenanceTs = mimbox.admin_page?.last_maintenance_timestamp;

          if (lastMaintenanceTs) {
            const d = new Date(lastMaintenanceTs);
            ret[mimbox.id] = {
              weekly: d,
              monthly: undefined,
            };
            return;
          }

          // Fallback to install date if no maintenance has ever been performed
          const installDate = mimbox.install?.date ? new Date(mimbox.install.date) : undefined;
          ret[mimbox.id] = {
            weekly: installDate,
            monthly: undefined,
          };
        }),
      );

      const MB2sPromises = getMaintenanceEvents({
        mmids: MB2s.map(({ mmid }) => mmid as string),
      }).then(({ data }) => {
        data.monthly.forEach((monthlyEvent) => {
          const mimbox = MB2s.find(({ mmid }) => monthlyEvent.mmid === mmid);
          if (!mimbox) return;

          const installDate = mimbox?.install?.date ? new Date(mimbox.install.date) : undefined;
          if (!ret[mimbox.id]) {
            ret[mimbox.id] = {
              monthly: installDate,
              weekly: installDate,
            };
          }
          if (monthlyEvent.ts) {
            ret[mimbox.id].monthly = new Date(monthlyEvent?.ts);
          }
        });
        data.weekly.forEach((weeklyEvent) => {
          const mimbox = MB2s.find(({ mmid }) => weeklyEvent.mmid === mmid);
          if (!mimbox) return;
          const installDate = mimbox?.install?.date ? new Date(mimbox.install.date) : undefined;
          if (!ret[mimbox.id]) {
            ret[mimbox.id] = {
              monthly: installDate,
              weekly: installDate,
            };
          }
          if (weeklyEvent.ts) {
            ret[mimbox.id].weekly = new Date(weeklyEvent?.ts);
          }
        });
      });
      const combined = [...MB1sPromises, MB2sPromises];

      await Promise.all(combined);
      const found = Object.keys(ret);
      const notFound = mimboxes.filter(({ id }) => !found.includes(id));
      notFound.forEach((mimbox) => {
        const installDate = mimbox?.install?.date ? new Date(mimbox.install.date) : undefined;
        if (!installDate) return;
        if (!mimbox.mmid) {
          ret[mimbox.id] = {
            weekly: installDate,
            monthly: undefined,
          };
        } else {
          ret[mimbox.id] = {
            weekly: installDate,
            monthly: installDate,
          };
        }
      });
      return ret;
    },
    async getMB2StoragePercentage(
      mmids: string[],
      interval?: { from: Date; to: Date },
    ): Promise<Record<string, number>> {
      if (!mmids.length) return {};

      const mmidsKey = [...mmids].sort().join(",");
      let cacheKey = `${mmidsKey}`;
      if (interval) {
        cacheKey += `-${interval.from.getTime()}-${interval.to.getTime()}`;
      }

      if (cacheKey in this.storagePercentagePromiseCache) {
        return this.storagePercentagePromiseCache[cacheKey];
      }

      const promise = (async () => {
        const result: Record<string, number> = {};
        const mmidsToCheck: string[] = [];

        mmids.forEach((mmid) => {
          let mmidCacheKey = `${mmid}`;
          if (interval) {
            mmidCacheKey += `-${interval.from.getTime()}-${interval.to.getTime()}`;
          }
          if (mmidCacheKey in this.storagePercentageCache) {
            result[mmid] = this.storagePercentageCache[mmidCacheKey];
          } else {
            mmidsToCheck.push(mmid);
          }
        });

        if (mmidsToCheck.length === 0) return result;

        let from = interval?.from;
        let to = interval?.to;
        const fmtFromDate = from?.toISOString();
        const fmtToDate = to?.toISOString();
        const { data } = await getStoragePercentage({
          mmids: mmidsToCheck,
          from: fmtFromDate,
          to: fmtToDate,
        });
        const preCalc = data.storagePercentageViews.reduce<
          Record<string, { storing: number; total: number }>
        >((acc, current) => {
          const mmid = current.mmid;
          if (!mmid) return acc;
          const discarding = current.discarding || 0;
          const storing = current.storing || 0;
          if (!acc[mmid]) {
            acc[mmid] = {
              storing: 0,
              total: 0,
            };
          }
          acc[mmid].storing += storing;
          acc[mmid].total += storing + discarding;
          return acc;
        }, {});

        Object.entries(preCalc).forEach(([mmid, { storing, total }]) => {
          let mmidCacheKey = `${mmid}`;
          if (interval) {
            mmidCacheKey += `-${interval.from.getTime()}-${interval.to.getTime()}`;
          }
          const calc = Number(((storing / total) * 100).toFixed(0));
          this.storagePercentageCache[mmidCacheKey] = calc;
          result[mmid] = calc;
        });
        return result;
      })();

      this.storagePercentagePromiseCache[cacheKey] = promise;
      try {
        return await promise;
      } finally {
        setTimeout(() => {
          delete this.storagePercentagePromiseCache[cacheKey];
        }, 500);
      }
    },
    async getMB2SWVersion(
      mmid: string,
    ): Promise<{ espFwVersion: string; cocpuFwVersion: string; ts: string }> {
      const metadataResult = await getMetadataFor({ mmid });
      const { espFwVersion, cocpuFwVersion, ts } = metadataResult.data.metadatas?.[0] || {};
      return {
        espFwVersion: espFwVersion || "N/A",
        cocpuFwVersion: cocpuFwVersion || "N/A",
        ts,
      };
    },
    async getMB2SWVersions(
      mmids: string[],
    ): Promise<Record<string, { espFwVersion: string; cocpuFwVersion: string; ts: string }>> {
      if (!mmids.length) return {};

      const mmidsKey = [...mmids].sort().join(",");
      if (mmidsKey in this.swVersionsPromiseCache) {
        return this.swVersionsPromiseCache[mmidsKey];
      }

      const promise = (async () => {
        const versions: Record<
          string,
          { espFwVersion: string; cocpuFwVersion: string; ts: string }
        > = {};
        const mmidsToCheck: string[] = [];

        mmids.forEach((mmid) => {
          if (mmid in this.swVersionsCache) {
            versions[mmid] = this.swVersionsCache[mmid];
          } else {
            mmidsToCheck.push(mmid);
          }
        });

        if (mmidsToCheck.length === 0) return versions;

        const metadataResult = await getMetadataForMmids({ mmids: mmidsToCheck });

        // We might have multiple metadata records per mmid, they are ordered by ts: DESC
        // so the first one we encounter is the latest
        metadataResult.data.metadatas?.forEach((meta) => {
          if (!versions[meta.mmid]) {
            const versionData = {
              espFwVersion: meta.espFwVersion || "N/A",
              cocpuFwVersion: meta.cocpuFwVersion || "N/A",
              ts: meta.ts,
            };
            versions[meta.mmid] = versionData;
            this.swVersionsCache[meta.mmid] = versionData;
          }
        });

        // Ensure all requested mmids have an entry
        mmids.forEach((mmid) => {
          if (!versions[mmid]) {
            const emptyVersion = { espFwVersion: "N/A", cocpuFwVersion: "N/A", ts: "" };
            versions[mmid] = emptyVersion;
            this.swVersionsCache[mmid] = emptyVersion;
          }
        });

        return versions;
      })();

      this.swVersionsPromiseCache[mmidsKey] = promise;
      try {
        return await promise;
      } finally {
        setTimeout(() => {
          delete this.swVersionsPromiseCache[mmidsKey];
        }, 500);
      }
    },
    async getWashingMachines(id: string) {
      const washingMachinesRef = collection(db, "washingmachines");
      const washingMachinesSnapshot = await getDocs(washingMachinesRef);
      let washingMachines: WashingMachine[] = [];
      washingMachinesSnapshot.docs.map((doc) => {
        const data = doc.data();
        data.machines.forEach((machine: WashingMachine) => {
          if (machine.connected === id) {
            washingMachines.push({
              id: machine.id,
              brand: doc.id,
              model: machine.model,
              type: machine.type,
              year: machine.year,
            });
          }
        });
      });
      return washingMachines;
    },
    async getRecyclingRateDelta(
      id: string,
      referenceDate: Date,
      stat: Stat,
    ): Promise<number | undefined> {
      const cacheKey = `${id}-${referenceDate.getTime()}`;
      if (cacheKey in this.deltaRRCache) {
        return this.deltaRRCache[cacheKey];
      }
      if (this.deltaRRPromiseCache && cacheKey in this.deltaRRPromiseCache) {
        return this.deltaRRPromiseCache[cacheKey];
      }

      const promise = (async () => {
        const AMOUNT_OF_DAYS = 14;
        const mimbox = this.mimboxes.find((mimbox) => mimbox.id === id);
        const mmid = mimbox?.mmid;

        const isMB2 = !!mmid;

        const endDelta = new Date(referenceDate);
        endDelta.setDate(referenceDate.getDay() - endDelta.getDay());
        const startDelta = new Date(endDelta);
        startDelta.setDate(endDelta.getDay() - AMOUNT_OF_DAYS);

        const beforeToDelta = new Date(startDelta);
        beforeToDelta.setMinutes(startDelta.getMinutes() - 1);
        const beforeFromDelta = new Date(beforeToDelta);
        beforeFromDelta.setMinutes(beforeToDelta.getHours() - 10);

        if (isMB2) {
          return new Promise<number | undefined>((resolve, reject) => {
            deltaRRQueue.push({
              mmid,
              stat,
              beforeFromDelta,
              beforeToDelta,
              startDelta,
              endDelta,
              resolve: (val) => {
                if (val !== undefined) this.deltaRRCache[cacheKey] = val;
                resolve(val);
              },
              reject,
            });

            if (!deltaRRTimeout) {
              deltaRRTimeout = setTimeout(async () => {
                const currentQueue = deltaRRQueue;
                deltaRRQueue = [];
                deltaRRTimeout = null;

                const mmids = [...new Set(currentQueue.map((q) => q.mmid))];
                // Take the timestamps from the first item since they are identical for the same referenceDate
                const { beforeFromDelta, beforeToDelta, startDelta, endDelta } = currentQueue[0];

                try {
                  const getMB2IntervalStats = httpsCallable(functions, "getMB2IntervalStats");

                  const [beforeDeltaRes, deltaRes] = await Promise.all([
                    getMB2IntervalStats({
                      mmids,
                      to: beforeToDelta.toISOString(),
                      from: beforeFromDelta.toISOString(),
                    }),
                    getMB2IntervalStats({
                      mmids,
                      to: endDelta.toISOString(),
                      from: startDelta.toISOString(),
                    }),
                  ]);

                  const beforeDeltaStats = beforeDeltaRes.data as Record<string, any>;
                  const deltaStats = deltaRes.data as Record<string, any>;

                  currentQueue.forEach(({ mmid, stat: qStat, resolve: qResolve }) => {
                    const beforeDeltaStat = beforeDeltaStats[mmid]?.statInRange;
                    const deltaStat = deltaStats[mmid]?.statInRange;
                    let statsDiff: RawStat = deltaStat;
                    if (qStat && beforeDeltaStat) {
                      statsDiff = { ...statsDiff, ...MB2StatsDiff(deltaStat, beforeDeltaStat) };
                    }
                    const prevRR = MB2RecyclingRate(statsDiff);
                    const currentRR = qStat.recyclingRate || 0;
                    const deltaCalc = currentRR - prevRR;
                    qResolve(deltaCalc);
                  });
                } catch (error) {
                  console.error(
                    "Failed to fetch MB2 recycling rate deltas in bulk from Callable:",
                    error,
                  );
                  currentQueue.forEach((q) => q.reject(error));
                }
              }, 50);
            }
          });
        }

        return new Promise<number | undefined>((resolve, reject) => {
          deltaRRMB1Queue.push({
            id,
            stat,
            startDelta,
            endDelta,
            resolve: (val) => {
              if (val !== undefined) this.deltaRRCache[cacheKey] = val;
              resolve(val);
            },
            reject,
          });

          if (!deltaRRMB1Timeout) {
            deltaRRMB1Timeout = setTimeout(async () => {
              const currentQueue = deltaRRMB1Queue;
              deltaRRMB1Queue = [];
              deltaRRMB1Timeout = null;

              const { startDelta, endDelta } = currentQueue[0];
              const formatHour = (d: Date) => {
                const year = d.getUTCFullYear();
                const month = String(d.getUTCMonth() + 1).padStart(2, "0");
                const day = String(d.getUTCDate()).padStart(2, "0");
                const hour = String(d.getUTCHours()).padStart(2, "0");
                return `${year}-${month}-${day}T${hour}`;
              };

              const startStr = formatHour(startDelta);
              const endStr = formatHour(endDelta);
              const totals: Record<string, { totalTap: number; totalRecycled: number }> = {};

              currentQueue.forEach((q) => (totals[q.id] = { totalTap: 0, totalRecycled: 0 }));

              try {
                if (startStr <= endStr) {
                  const q = query(
                    collection(db, "mb1_hourly_stats"),
                    and(where(documentId(), ">=", startStr), where(documentId(), "<=", endStr)),
                  );
                  const snapshot = await getDocs(q);
                  snapshot.forEach((doc) => {
                    const data = doc.data();
                    currentQueue.forEach((qItem) => {
                      if (data[qItem.id]) {
                        totals[qItem.id].totalTap += data[qItem.id].tap || 0;
                        totals[qItem.id].totalRecycled += data[qItem.id].recycled || 0;
                      }
                    });
                  });
                }

                currentQueue.forEach(({ id: queueId, stat: qStat, resolve: qResolve }) => {
                  const { totalTap, totalRecycled } = totals[queueId];
                  const totalWater = (totalTap || 0) + (totalRecycled || 0);
                  const prevRR =
                    totalWater > 0
                      ? Number((((totalRecycled || 0) / totalWater) * 100).toFixed(2))
                      : 0;
                  const currentRR = qStat.recyclingRate || 0;
                  const deltaCalc = currentRR - prevRR;
                  qResolve(deltaCalc);
                });
              } catch (error) {
                console.error("Failed to fetch MB1 recycling rate deltas in bulk:", error);
                currentQueue.forEach((q) => q.reject(error));
              }
            }, 50);
          }
        });
      })();

      if (!this.deltaRRPromiseCache) {
        this.deltaRRPromiseCache = {};
      }
      this.deltaRRPromiseCache[cacheKey] = promise;

      try {
        await promise;
      } finally {
        delete this.deltaRRPromiseCache[cacheKey];
      }

      return promise;
    },
    async getServiceLogs(id: string): Promise<ServiceLog[]> {
      const service_log_col = query(
        collection(db, "mimboxes", id, "service_log"),
        orderBy("timestamp", "desc"),
      );
      const snapshot = await getDocs(service_log_col);
      const logs: ServiceLog[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({ timestamp: data.timestamp || Date.now(), ...data });
      });
      return logs;
    },
    async toggleTag(mimboxId: string, tag: MimboxTag) {
      const mimboxDoc = await getDoc(doc(db, "mimboxes", mimboxId).withConverter(mimboxConverter));

      const mimbox = mimboxDoc.data()?.dump();
      if (!mimbox) return;
      // Plain tags are stored in `tags` and colored ones in `newTags`
      const storedNewTags: MimboxTag[] = (mimbox as { newTags?: MimboxTag[] }).newTags || [];
      let key = "tags";
      let newTags: MimboxTag[] = [];
      if (typeof tag != "string") {
        const tags = storedNewTags.filter((tag) => typeof tag != "string") as {
          name: string;
        }[];
        key = "newTags";
        const existingTagIndex = tags.findIndex(({ name }) => name === tag.name);
        if (existingTagIndex < 0) {
          newTags = [...tags, tag];
        } else {
          newTags = tags.filter(({ name }) => name != tag.name);
        }
      } else {
        const tags = (mimbox?.tags || []).filter((tag) => typeof tag === "string");

        const snewTags = new Set([...tags]);
        if (snewTags.has(tag)) {
          snewTags.delete(tag);
        } else {
          snewTags.add(tag);
        }
        newTags = [...snewTags];
      }
      const mimboxRef = doc(db, "mimboxes", mimboxId);
      await updateDoc(mimboxRef, {
        [key]: newTags,
      });

      const plainTags = (mimbox?.tags || []).filter((t) => typeof t === "string");
      return {
        ...mimbox,
        tags: key === "newTags" ? [...plainTags, ...newTags] : [...newTags, ...storedNewTags],
      };
    },

    async createServiceLog(
      id: string,
      data: { files?: File[]; [key: string]: any },
      timestamp: number,
      wasServiced: boolean,
    ) {
      let { files, ...uploadData } = data;
      const service_log_col = collection(db, "mimboxes", id, "service_log");
      if (files) {
        uploadData.images = await Promise.all(
          files.map(async (image: File) => {
            const [downloadURL, error] = await uploadImage(`mimboxes/${id}`, image);
            if (error) {
              if (sentryClient) {
                const scope = new Sentry.Scope();
                scope.setExtras({
                  size: image.size,
                  name: image.name,
                  mime: image.type,
                });
                (sentryClient as ReturnType<typeof Sentry.init>)?.captureException(
                  error,
                  {},
                  scope,
                );
              }
              throw error;
            }
            return downloadURL;
          }),
        );
      }

      if (wasServiced) {
        const mimboxRef = doc(db, "mimboxes", id);
        await setDoc(
          mimboxRef,
          { admin_page: { last_service_timestamp: timestamp } },
          { merge: true },
        );
      }

      return await addDoc(service_log_col, {
        ...uploadData,
        created_at: serverTimestamp(),
      });
    },
    async updateServiceLog(
      mimboxId: string,
      logId: string,
      update: Record<string, any>,
      storeHistory: Record<string, any>,
    ) {
      const logRef = doc(db, "mimboxes", mimboxId, "service_log", logId);
      try {
        await updateDoc(logRef, { ...update, updated_at: serverTimestamp() });
      } catch (e) {
        console.error("Unable to update log", logId, e);
        throw e;
      }
      const logHistoryCollection = collection(
        db,
        "mimboxes",
        mimboxId,
        "service_log",
        logId,
        "history",
      );

      await addDoc(logHistoryCollection, { ...storeHistory, created_at: serverTimestamp() });
    },

    async getEolLogs(id: string): Promise<EolLog[]> {
      const eol_log_col = query(
        collection(db, "mimboxes", id, "eol_log"),
        orderBy("timestamp", "desc"),
      );
      const snapshot = await getDocs(eol_log_col);
      const logs: EolLog[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        logs.push({ timestamp: data.timestamp || Date.now(), ...data });
      });
      return logs;
    },
    async getLog(id: string): Promise<any> {
      const logFunc = httpsCallable(functions, "generate_mimbox_log");
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let result = await logFunc({ id, timeZone });
      return result.data;
    },
    async refreshMimbox(id: string): Promise<Mimbox | null> {
      const authStore = useAuthStore();
      const mimbox = doc(db, "mimboxes", id);
      const mimboxSnapshot = await getDoc(mimbox);
      const data = await getDataForMimbox(
        mimboxSnapshot,
        authStore.is.staff(),
        authStore.is.staff(),
      );
      this.setMimbox(data as Mimbox);
      if (this.selectedMimbox?.id === data.id) this.selectedMimbox = data as Mimbox;
      return data as Mimbox;
    },
    async sendRpc(mmid: string, rpc: string) {
      //TODO: Add multiple rpc at a time functionality to this function. Right now it will overwrite.
      const authStore = useAuthStore();
      if (!authStore.is.staff()) throw new Error("Only staff can send RPC commands");
      const data = {
        downlink: {
          config_id: 200,
          rpc: [rpc],
        },
      };
      const q = query(collection(db, "mimboxes"), where("mmid", "==", mmid));
      const snapshot = await getDocs(q);
      if (snapshot.empty) throw new Error(`No mimbox found with mmid ${mmid}`);
      const mimboxRef = snapshot.docs[0].ref;
      await updateDoc(mimboxRef, data);
    },
    async customerUpdateMimbox(id: string, install: Install) {
      const mimboxRef = doc(db, "mimboxes", id);
      await setDoc(mimboxRef, { install }, { merge: true });
      await this.refreshMimbox(id);
    },
    async changeInstallationStatus(id: string, status: string, installDate?: Date) {
      const mimboxRef = doc(db, "mimboxes", id);
      await setDoc(
        mimboxRef,
        {
          status,
          install: { date: installDate ? installDate.toISOString() : undefined },
        },
        { merge: true },
      );
      return await this.refreshMimbox(id);
    },
    async updateMimbox(data: Partial<MimboxData>) {
      const files = data.files;
      const { files: ignoreFiles, id, qrLink, ...uploadData } = JSON.parse(JSON.stringify(data));
      if (!id) throw new Error("Mimbox ID is required");

      const mimboxRef = doc(db, "mimboxes", id);
      const currentMimbox = (await getDoc(mimboxRef))?.data();

      uploadData.access = setAccess(uploadData, currentMimbox as Mimbox);
      if (files && files.length > 0) {
        const links = await Promise.all(
          files.map(async (image: File) => {
            const [downloadURL, error] = await uploadImage(`mimboxes/${id}`, image);
            if (error) throw new Error("Error uploading images");
            return downloadURL;
          }),
        );
        uploadData.images = [...(uploadData.images || []), ...links];
      }
      await setDoc(mimboxRef, uploadData, { merge: true });
      await this.refreshMimbox(id);
    },
    async addMimbox(data: MimboxData) {
      const files = data?.files;
      const { files: ignoreFiles, qrLink, ...uploadData } = JSON.parse(JSON.stringify(data));
      const id = crypto.randomUUID();

      uploadData.access = setAccess(uploadData);
      uploadData.hidePerformanceData = true;

      const mimboxRef = doc(db, "mimboxes", id);
      if (files) {
        uploadData.images = await Promise.all(
          files.map(async (image: File) => {
            const [downloadURL, error] = await uploadImage(`mimboxes/${id}`, image);
            if (error) throw new Error("Error uploading images");
            return downloadURL;
          }),
        );
      }
      await setDoc(mimboxRef, { ...uploadData, id });
      const box = await this.refreshMimbox(id);
      this.selectedMimbox = box;
      return id;
    },
    async deleteMimbox(id: string) {
      if (!id) throw new Error("Mimbox ID is required");

      // Delete the mimbox document
      const mimboxRef = doc(db, "mimboxes", id);
      await deleteDoc(mimboxRef);

      // Remove from local state
      this.mimboxes = this.mimboxes.filter((m) => m.id !== id);
      if (this.selectedMimbox?.id === id) {
        this.selectedMimbox = null;
      }

      return true;
    },
    async unsubscribeForNotification(
      mimboxId: string,
      notificationSetting: keyof typeof NOTIFICATION_SETTING,
      entityId: string,
      subscriber: Subscriber,
    ) {
      const subscriberRef = doc(
        db,
        "notification_settings",
        notificationSetting,
        "mimboxes",
        mimboxId,
        "subscribers",
        entityId,
      );
      const mimboxRef = doc(db, "notification_settings", notificationSetting, "mimboxes", mimboxId);
      const mimboxSnapshot = await getDoc(mimboxRef);
      if (!mimboxSnapshot.exists()) return true;
      try {
        await setDoc(
          subscriberRef,
          {
            subscribers: arrayRemove(subscriber),
          },
          { merge: true },
        );
        return true;
      } catch (error) {
        console.error(`Unable to subscribe to ${notificationSetting}`, error);
        return false;
      }
    },
    async subscribeForNotification(
      mimboxId: string,
      notificationSetting: keyof typeof NOTIFICATION_SETTING,
      entityId: string,
      subscriber: Subscriber,
    ): Promise<boolean> {
      const subscriberRef = doc(
        db,
        "notification_settings",
        notificationSetting,
        "mimboxes",
        mimboxId,
        "subscribers",
        entityId,
      );
      const mimboxRef = doc(db, "notification_settings", notificationSetting, "mimboxes", mimboxId);
      const mimboxSnapshot = await getDoc(mimboxRef);
      if (!mimboxSnapshot.exists()) {
        await setDoc(mimboxRef, {
          lastSent: null,
          lastSentId: null,
        });
      }
      try {
        const subscriberDoc = await getDoc(subscriberRef);
        if (subscriberDoc.exists()) {
          const subs = subscriberDoc.data()?.subscribers as Record<string, any>[];
          const index = subs.findIndex((sub) => {
            return (
              (subscriber.email && subscriber.email === sub.email) ||
              subscriber.name === sub.name ||
              (subscriber.phone && subscriber.phone === sub.phone)
            );
          });
          if (index >= 0) {
            subs[index] = subscriber;
            await setDoc(
              subscriberRef,
              {
                subscribers: subs,
              },
              { merge: true },
            );
            return true;
          }
        }
        await setDoc(
          subscriberRef,
          {
            subscribers: arrayUnion(subscriber),
          },
          { merge: true },
        );
        return true;
      } catch (error) {
        console.error(`Unable to subscribe to ${notificationSetting}`, error);
        return false;
      }
    },
    async getNotificationSettings(mimboxId: string, entityId: string) {
      const settings = Object.keys(NOTIFICATION_SETTING) as (keyof typeof NOTIFICATION_SETTING)[];
      const notificationSettings: NotificationSetting[] = [];
      const promises = settings.map(async (setting) => {
        const subscriberRef = doc(
          db,
          "notification_settings",
          setting,
          "mimboxes",
          mimboxId,
          "subscribers",
          entityId,
        );
        const subscriberSnapshot = await getDoc(subscriberRef);
        const companySubscribers = subscriberSnapshot.data() || { subscribers: [] };
        notificationSettings.push({
          notificationSetting: setting,
          display: NOTIFICATION_SETTING[setting],
          subscribers: companySubscribers?.subscribers,
        });
      });
      await Promise.all(promises);
      return notificationSettings;
    },
    async subscribeToC1s() {
      const authStore = useAuthStore();
      if (!authStore.is.staff()) return;
      this.unsubscribeFromC1s();

      const c1sQuery = query(collection(db, "mimboxes"), where("mmid", "!=", null));
      this.unsubscribeC1s = onSnapshot(c1sQuery, (snapshot) => {
        const ret: C1Data[] = [];
        snapshot.forEach((doc) => prepareC1Array(doc, ret));
        this.c1s = ret;
      });
    },
    unsubscribeFromC1s() {
      if (this.unsubscribeC1s) {
        this.unsubscribeC1s();
        this.unsubscribeC1s = null;
      }
    },
  },
});

function prepareC1Array(mimbox: any, ret: C1Data[]) {
  const data = mimbox.data();
  ret.push({
    name: data.name || "",
    ...data,
  });
}

function setAccess(data: Partial<MimboxData>, currentMimbox?: Mimbox) {
  const values: string[] = ["mimbly"];

  if (data.install?.customer) values.push(data.install.customer);
  else if (!data?.install?.customer && currentMimbox?.install?.customer) {
    currentMimbox.access = currentMimbox.access.filter(
      (str) => str !== currentMimbox?.install?.customer,
    );
  }
  if (Array.isArray(data.access)) {
    values.push(...data.access);
    return [...new Set([...values])];
  }
  // Unsure reason for adding currentMimbox access here.
  // Keep for now in case it's for covering some edge case.
  else {
    return [...new Set([...values, ...(currentMimbox?.access ?? [])])];
  }
}

/** FIXME: With new access levels and logic this function might not be needed */
export function filterAccessList(_item: string) {
  return true;
  /*
   * OLD IMPLEMENTATION
  const authStore = useAuthStore();
  const hideFromAccess = ["mimbly"];
  if (authStore.organization && authStore.organization.client) {
    hideFromAccess.push(authStore.organization.client);
  }
  return !hideFromAccess.includes(item);
    */
}

async function getDataForMimbox(
  mimboxdoc: any,
  isAdmin: boolean,
  isStaff: boolean,
): Promise<Mimbox | Partial<Mimbox>> {
  const data = mimboxdoc.data();
  if (isAdmin) {
    let tags: string[] = data.tags || [];
    let newTags: { name: string; color?: string }[] = data.newTags || [];
    let internalName = data?.internalName;
    if (!isStaff) {
      tags = tags.filter((tag) => tag != "pilot");
      internalName = undefined;
    }
    return {
      id: mimboxdoc.id,
      access: data.access || [],
      hidePerformanceData: data.hidePerformanceData,
      mimbox_vers: data.mimbox_vers,
      mmid: data.mmid,
      iccid: data.iccid,
      admin_page: data.admin_page,
      images: data.images,
      install: data.install,
      serial_nr: data.serial_nr,
      sw_ver: data.sw_ver,
      water_saved: data.water_saved,
      water_used: data.water_used,
      updatedAt: data.last_alive,
      status: data.status,
      tags: [...tags, ...newTags].filter((tag) => {
        if (typeof tag === "string") return !!tag;
        return !!tag.name;
      }),
      internalName,
    };
  } else {
    const performaceData = {
      water_saved: data.hidePerformanceData ? null : data.water_saved,
      water_used: data.hidePerformanceData ? null : data.water_used,
      admin_page: {
        last_service_timestamp: data.admin_page?.last_service_timestamp,
        last_error: {
          error_code: data.hidePerformanceData ? null : data.admin_page?.last_error?.error_code,
        },
        last_maintenance_timestamp: data?.admin_page?.last_maintenance_timestamp,
      },
    };

    return {
      id: mimboxdoc.id,
      access: data.access || [],
      hidePerformanceData: data.hidePerformanceData,
      mimbox_vers: data?.mimbox_vers ? (data.mimbox_vers === "MB2" ? "MB2" : "MB1") : "-",
      mmid: data.mmid,
      images: data.images,
      install: data.install,
      serial_nr: data.serial_nr,
      sw_ver: data.sw_ver,
      updatedAt: data.last_alive,
      status: data.status,
      tags: [],
      ...performaceData,
    };
  }
}
